import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronRight, Globe, Plus, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import webScraperService, {
  WebScrapeJob,
  WebScraperJobStatus,
} from "../../services/web-scraper.service";
import documentStoresService from "../../services/document-stores.service";
import JobStatusBadge from "../../components/documents/web-scraper/JobStatusBadge";
import CreateJobModal from "../../components/documents/web-scraper/CreateJobModal";
import DeleteConfirmModal from "../../components/documents/DeleteConfirmModal";

const PAGE_SIZE = 10;

export default function WebScraperJobList() {
  const { documentStoreId } = useParams<{ documentStoreId: string }>();
  const navigate = useNavigate();

  const [storeName, setStoreName] = useState("");
  const [jobs, setJobs] = useState<WebScrapeJob[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<WebScraperJobStatus | "">("");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<WebScrapeJob | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!documentStoreId) return;
    documentStoresService.getDocumentStoreById(documentStoreId)
      .then((store) => setStoreName(store.folder_name))
      .catch(() => {});
  }, [documentStoreId]);

  const fetchJobs = useCallback(async (page: number, search: string, status: WebScraperJobStatus | "") => {
    if (!documentStoreId) return;
    setLoading(true);
    try {
      const result = await webScraperService.listJobs({
        document_store_id: documentStoreId,
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setJobs(result.list);
      setTotalCount(result.total);
    } catch {
      toast.error("Failed to load scrape jobs");
    } finally {
      setLoading(false);
    }
  }, [documentStoreId]);

  useEffect(() => {
    fetchJobs(currentPage, searchTerm, statusFilter);
  }, [fetchJobs, currentPage, searchTerm, statusFilter]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await webScraperService.deleteJob(deleteTarget._id);
      toast.success("Job deleted");
      setDeleteTarget(null);
      fetchJobs(currentPage, searchTerm, statusFilter);
    } catch {
      toast.error("Failed to delete job");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreated = (job: WebScrapeJob) => {
    setShowCreateModal(false);
    navigate(`/files/${documentStoreId}/web-scraper/${job._id}`);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <>
      <div className="mx-auto space-y-3">

        {/* Page header card */}
        <div className="bg-white border border-gray-200 px-5 py-4 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Web Scraper</h1>
                <p className="text-sm text-gray-400 mt-0.5">Crawl websites and import files into "{storeName}"</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/files/${documentStoreId}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 active:scale-95 transition-all"
                type="button"
              >
                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                Back
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 active:scale-95 transition-all"
                type="button"
              >
                <Plus className="w-3.5 h-3.5" />
                Create New Job
              </button>
            </div>
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as WebScraperJobStatus | ""); setCurrentPage(1); }}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              <option value="">All Statuses</option>
              {Object.values(WebScraperJobStatus).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by URL…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-8 pr-4 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 w-52"
              />
            </div>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Seed URL</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pages</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Files Found</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Imported</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && jobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" />
                  </td>
                </tr>
              ) : jobs.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <Globe className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No scrape jobs yet</p>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="mt-2 text-xs text-purple-600 hover:underline"
                      type="button"
                    >
                      Create your first job
                    </button>
                  </td>
                </tr>
              ) : (
                jobs.map((job) => (
                  <tr
                    key={job._id}
                    onClick={() => navigate(`/files/${documentStoreId}/web-scraper/${job._id}`)}
                    className="hover:bg-purple-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5 max-w-[260px]">
                      <span className="text-gray-900 font-medium text-sm truncate block" title={job.seed_url}>
                        {job.seed_url}
                      </span>
                      <span className="text-gray-400 text-xs">{job.allowed_domain}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <JobStatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-sm">{job.pages_crawled}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-sm">{job.files_discovered}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-sm">{job.items_imported}</td>
                    <td className="px-4 py-2.5 text-gray-400 text-sm whitespace-nowrap">{formatDate(job.createdAt)}</td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(job); }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        type="button"
                        title="Delete job"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-500">
                {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded text-xs border border-gray-200 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  type="button"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded text-xs border border-gray-200 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Modals rendered outside space-y wrapper to avoid inherited margin-top */}
      <CreateJobModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        documentStoreId={documentStoreId!}
        storeName={storeName}
        onCreated={handleCreated}
      />
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        itemName={deleteTarget?.seed_url ?? ""}
        itemType="Scrape Job"
        isDeleting={isDeleting}
      />
    </>
  );
}
